if (!customElements.get("product-media-slider")) {
  customElements.define(
    "product-media-slider",
    class ProductMediaSlider extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.slider = this.querySelector('[id^="Slider-"]');
        this.currentPageElement = this.querySelector(
          ".slider-counter--current"
        );
        this.pageTotalElement = this.querySelector(".slider-counter--total");
        this.prevButton = this.querySelector('button[name="previous"]');
        this.nextButton = this.querySelector('button[name="next"]');

        if (!this.slider) return;

        this.onScroll = this.update.bind(this);
        this.onButtonClick = this.onButtonClick.bind(this);
        this.resizeObserver = new ResizeObserver(() => this.initPages());

        this.slider.addEventListener("scroll", this.onScroll, {
          passive: true,
        });
        this.prevButton?.addEventListener("click", this.onButtonClick);
        this.nextButton?.addEventListener("click", this.onButtonClick);
        this.resizeObserver.observe(this.slider);

        this.initPages();
      }

      disconnectedCallback() {
        if (!this.initialized) return;

        this.slider?.removeEventListener("scroll", this.onScroll);
        this.prevButton?.removeEventListener("click", this.onButtonClick);
        this.nextButton?.removeEventListener("click", this.onButtonClick);
        this.resizeObserver?.disconnect();
        this.initialized = false;
      }

      initPages() {
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.sliderItemsToShow = Array.from(this.sliderItems).filter(
          (element) => element.clientWidth > 0
        );
        this.totalPages = this.sliderItemsToShow.length;
        // Every slide is as wide as the track and the gap between them is
        // the same, so one step is the distance between two slides. It is
        // measured here, on load and on resize, so a scroll reads no slide
        // (decision 243).
        this.firstSlideOffset = this.sliderItemsToShow[0]?.offsetLeft || 0;
        this.pageStep =
          this.totalPages > 1
            ? Math.abs(
                this.sliderItemsToShow[1].offsetLeft - this.firstSlideOffset
              )
            : this.slider.clientWidth;

        if (this.totalPages === 0) {
          this.currentPage = 0;
          this.updateCounter();
          return;
        }

        this.currentPage = this.getCurrentPage();
        this.updateCounter();
        this.updateButtonStates();
      }

      resetPages() {
        this.initPages();
      }

      update() {
        if (!this.slider || this.totalPages === 0) return;

        const previousPage = this.currentPage;
        this.currentPage = this.getCurrentPage();
        this.updateButtonStates();

        // The counter text changes only when the page does, not on every
        // scroll event (decision 243).
        if (this.currentPage === previousPage) return;

        this.updateCounter();
        this.dispatchEvent(
          new CustomEvent("slideChanged", {
            detail: {
              currentPage: this.currentPage,
              currentElement: this.sliderItemsToShow[this.currentPage - 1],
            },
          })
        );
      }

      getCurrentPage() {
        if (!this.sliderItemsToShow.length) return 0;
        if (!this.pageStep) return 1;

        // The nearest slide to the scroll position. In a right-to-left track
        // `scrollLeft` and the offsets run negative, so the distance is
        // taken as a size.
        const index = Math.round(
          Math.abs(this.slider.scrollLeft - this.firstSlideOffset) /
            this.pageStep
        );
        return Math.min(Math.max(index, 0), this.totalPages - 1) + 1;
      }

      updateCounter() {
        if (this.currentPageElement) {
          this.currentPageElement.textContent = this.currentPage;
        }

        if (this.pageTotalElement) {
          this.pageTotalElement.textContent = this.totalPages;
        }
      }

      updateButtonStates() {
        if (!this.prevButton && !this.nextButton) return;
        if (this.totalPages < 2) {
          this.prevButton?.setAttribute("disabled", "disabled");
          this.nextButton?.setAttribute("disabled", "disabled");
          return;
        }

        if (this.isSlideVisible(this.sliderItemsToShow[0])) {
          this.prevButton?.setAttribute("disabled", "disabled");
        } else {
          this.prevButton?.removeAttribute("disabled");
        }

        if (
          this.isSlideVisible(
            this.sliderItemsToShow[this.sliderItemsToShow.length - 1]
          )
        ) {
          this.nextButton?.setAttribute("disabled", "disabled");
        } else {
          this.nextButton?.removeAttribute("disabled");
        }
      }

      isSlideVisible(element, offset = 0) {
        if (!element) return false;

        const lastVisibleSlide =
          this.slider.clientWidth + this.slider.scrollLeft - offset;
        return (
          element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
          element.offsetLeft >= this.slider.scrollLeft
        );
      }

      setActiveElement(element, options = {}) {
        const activeIndex = this.sliderItemsToShow.indexOf(element);
        if (activeIndex < 0) return;

        if (options.scroll !== false) {
          this.setSlidePosition(element.offsetLeft);
        }

        this.currentPage = activeIndex + 1;
        this.updateCounter();
        this.updateButtonStates();
      }

      onButtonClick(event) {
        event.preventDefault();
        if (!this.sliderItemsToShow.length) return;

        const step = parseInt(event.currentTarget.dataset.step, 10) || 1;
        const currentIndex = Math.max(0, this.currentPage - 1);
        const targetIndex =
          event.currentTarget.name === "next"
            ? currentIndex + step
            : currentIndex - step;
        const target = this.sliderItemsToShow[
          Math.min(Math.max(targetIndex, 0), this.totalPages - 1)
        ];

        if (target) this.setActiveElement(target);
      }

      setSlidePosition(position) {
        this.slider.scrollTo({
          left: position,
        });
      }
    }
  );
}

if (!customElements.get("product-media-gallery")) {
  customElements.define(
    "product-media-gallery",
    class ProductMediaGallery extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia("(min-width: 5000px)");
        this.abortController = new AbortController();

        this.elements.viewer?.addEventListener(
          "slideChanged",
          this.debounce(this.onSlideChanged.bind(this), 100),
          { signal: this.abortController.signal }
        );

        if (this.elements.thumbnails) {
          for (const mediaToSwitch of this.elements.thumbnails.querySelectorAll(
            "[data-target]"
          )) {
            mediaToSwitch
              .querySelector("button")
              ?.addEventListener(
                "click",
                () => this.setActiveMedia(mediaToSwitch.dataset.target, false),
                { signal: this.abortController.signal }
              );
          }
        }

        if (
          this.elements.thumbnails &&
          this.dataset.desktopLayout.includes("thumbnail") &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }

        const activeMedia =
          this.elements.viewer?.querySelector(".product__media-item.is-active") ||
          this.elements.viewer?.querySelector("[data-media-id]");
        if (activeMedia) {
          this.elements.viewer?.setActiveElement?.(activeMedia, {
            scroll: false,
          });
        }
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.initialized = false;
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement?.dataset.mediaId;
        if (!mediaId) return;

        this.setActiveMedia(mediaId, false, {
          announce: false,
          play: false,
          preventStickyHeader: false,
          scrollPage: false,
          scrollSlider: false,
        });
      }

      setActiveMedia(mediaId, prepend, options = {}) {
        const settings = {
          announce: true,
          play: true,
          preventStickyHeader: true,
          scrollPage: true,
          scrollSlider: true,
          ...options,
        };
        const activeMedia =
          this.elements.viewer?.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer?.querySelector("[data-media-id]");
        if (!activeMedia) return;

        this.elements.viewer
          ?.querySelectorAll("[data-media-id]")
          .forEach((element) => {
            element.classList.remove("is-active");
          });
        activeMedia.classList.add("is-active");

        if (prepend) {
          if (activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );
            if (
              activeThumbnail &&
              activeThumbnail.parentElement.firstChild !== activeThumbnail
            ) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          this.elements.viewer?.resetPages?.();
        }

        this.elements.viewer?.setActiveElement?.(activeMedia, {
          scroll: settings.scrollSlider,
        });

        if (settings.preventStickyHeader) {
          this.preventStickyHeader();
        }

        if (settings.scrollPage) {
          window.setTimeout(() => {
            const activeMediaRect = activeMedia.getBoundingClientRect();
            if (activeMediaRect.top > -0.5) return;

            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: "smooth" });
          });
        }

        if (settings.play) {
          this.playActiveMedia(activeMedia);
        }

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );
        this.setActiveThumbnail(activeThumbnail);

        if (settings.announce && activeThumbnail) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaPosition
          );
        }
      }

      syncMediaFromLightbox(mediaId) {
        this.setActiveMedia(mediaId, false, {
          announce: false,
          play: false,
          preventStickyHeader: false,
          scrollPage: false,
        });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));
        thumbnail.querySelector("button")?.setAttribute("aria-current", true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          ".product__modal-opener--image img"
        );
        if (!image || !this.elements.liveRegion) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              "[index]",
              position
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer?.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation")
        );
      }

      debounce(fn, wait) {
        let timeout;
        return (...args) => {
          clearTimeout(timeout);
          timeout = setTimeout(() => fn.apply(this, args), wait);
        };
      }
    }
  );
}
